import { useId, useState, type FormEvent } from "react";
import { useChangeOwnPassword } from "@/api/users";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

export const PASSWORDS_DIFFER = "The new passwords do not match.";

export function PasswordPanel() {
  const id = useId();
  const change = useChangeOwnPassword();
  const { values, set, setValues } = useFormValues({ current: "", next: "", confirm: "" });
  const { error, errors, saving, run } = useSubmitState();
  const [mismatch, setMismatch] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setDone(false);
    if (values.next !== values.confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    const ok = await run(() => change.mutateAsync({ current: values.current, next: values.next }));
    if (ok) {
      setValues({ current: "", next: "", confirm: "" });
      setDone(true);
    }
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Change your password</CardTitle>
        <CardDescription>
          Changing it signs you out on your other devices; this one asks you to sign in again
          within half an hour.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(event) => void handleSubmit(event)} noValidate className="flex flex-col gap-4">
          {done && <Alert variant="success">Your password has been changed.</Alert>}
          <RequestError error={error} />
          <TextField
            id={`${id}-current`}
            label="Current password"
            type="password"
            autoComplete="current-password"
            value={values.current}
            onChange={(value) => set("current", value)}
            error={errors.current_password}
          />
          <TextField
            id={`${id}-next`}
            label="New password"
            type="password"
            autoComplete="new-password"
            value={values.next}
            onChange={(value) => set("next", value)}
            error={errors.new_password}
            hint="At least 12 characters"
          />
          <TextField
            id={`${id}-confirm`}
            label="Repeat the new password"
            type="password"
            autoComplete="new-password"
            value={values.confirm}
            onChange={(value) => set("confirm", value)}
            error={mismatch ? PASSWORDS_DIFFER : undefined}
          />
          <FormActions saving={saving} submitLabel="Change password" />
        </form>
      </CardContent>
    </Card>
  );
}
